import { i } from "@instantdb/react";
import { StatusFind } from "@wppconnect-team/wppconnect";

type PushSubscription = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export type WhatsappConnectionEvent =
  | StatusFind
  | "pod is down"
  | "autocloseCalled"
  | "browserClose"
  | "disconnectedMobile"
  | "phoneNotConnected"
  | "qrReadError"
  | "qrReadFail"
  | "serverClose"
  | "provisioningServer"
  | "waitingForSsh"
  | "serverProvisioned"
  | "startingDocker"
  | "dockerStarted";

export const whatsappAccountStatus = {
  connected: "inChat" as const,
  podIsDown: "pod is down" as const,
  startingDocker: "startingDocker" as const,
  dockerStarted: "dockerStarted" as const,
  provisioningServer: "provisioningServer" as const,
  waitingForSsh: "waitingForSsh" as const,
  serverProvisioned: "serverProvisioned" as const,
};

const _schema = i.schema({
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.any(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed(),
    }),
    users: i.entity({
      email: i.string().unique().indexed(),
      pushSubscriptions: i.json<Record<string, PushSubscription>>(),
      lastSeen: i.number().indexed(),
      whatsapp: i.string().optional().indexed(),
      monthlyPayment: i.number().optional().indexed(),
      paidCapacity: i.number().optional().indexed(),
      createdAt: i.number().indexed(),
      trialWarningLastSentAt: i.number().optional().indexed(),
    }),
    whatsappUsers: i.entity(
      { whatsappId: i.string().unique().indexed() },
    ),
    groups: i.entity({
      whatsappId: i.string().unique().indexed(),
      name: i.string().indexed().optional().indexed(),
    }),
    chats: i.entity({
      chatId: i.string().indexed().unique(),
      network: i.string().indexed(),
    }),
    webhooks: i.entity({
      name: i.string().indexed().unique(),
      url: i.string().indexed(),
    }),
    whatsappAccounts: i.entity({
      name: i.string().indexed(),
      phoneNumber: i.string().indexed().unique(),
      webhook: i.string().indexed().optional(),
      notes: i.string().optional().indexed(),
      port: i.number().indexed().optional(),
      statusHistory: i.json<
        Array<{ status: WhatsappConnectionEvent; timestamp: number }>
      >(),
      lastEventTime: i.number().indexed().optional(),
      verificationCode: i.string().optional().unique().indexed(),
      qrIssuedAt: i.number().optional().indexed(),
      pairingCode: i.string().optional().indexed(),
      pairingCodeIssuedAt: i.number().optional().indexed(),
      isConnected: i.boolean().indexed(),
      disconnectedAt: i.number().optional().indexed(),
      disconnectNotifiedAt: i.number().optional().indexed(),
      secretToken: i.string().indexed().unique(),
      dockerImage: i.string().optional().indexed(),
      isEnabled: i.boolean().optional().indexed(),
      disabledAt: i.number().optional().indexed(),
      webhookUrls: i.json<string[]>().optional(),
    }),
    phoneNumbers: i.entity({
      number: i.string().indexed().unique(),
      createdAt: i.number().indexed(),
      provider: i.string().indexed().optional(),
    }),
    staticProxies: i.entity({
      oxylabsPort: i.number().indexed().unique(),
    }),
    servers: i.entity({
      ip: i.string().unique().indexed(),
      providerId: i.string().unique().indexed(),
      name: i.string().indexed(),
      provider: i.string().indexed(),
      capacity: i.number().indexed().optional(),
    }),
  },
  links: {
    whatsappAccountServer: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "server",
      },
      reverse: {
        on: "servers",
        has: "many",
        label: "whatsappAccounts",
      },
    },
    whatsappAccountStaticProxies: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "staticProxy",
      },
      reverse: {
        on: "staticProxies",
        has: "one",
        label: "whatsappAccount",
      },
    },
    whatsappAccountsAdmins: {
      forward: {
        on: "whatsappAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "whatsappAccounts" },
    },
    phoneNumbersAdmins: {
      forward: {
        on: "phoneNumbers",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "adminPhoneNumbers" },
    },
    phoneNumberBillingUser: {
      forward: {
        on: "phoneNumbers",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingPhoneNumbers",
      },
    },
    whatsappAccountBillingUser: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingAccounts",
      },
    },
    chatWebhooks: {
      forward: { on: "chats", label: "webhooks", has: "many" },
      reverse: { on: "webhooks", label: "chats", has: "many" },
    },
    usersGroups: {
      forward: { on: "users", has: "many", label: "groups" },
      reverse: { on: "groups", has: "many", label: "members" },
    },
  },
  rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
